import { useState, MouseEvent } from 'react';
import {
  ListItem,
  ListItemIcon,
  Typography,
  Box,
  makeStyles,
  createStyles,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Draggable } from 'react-beautiful-dnd';

import SharedIndicator from 'src/components/sidebar/SharedIndicator';
import { CampaignProps } from 'src/types/campaign';

import { ReactComponent as CampaignsIcon } from 'src/assets/icons/campaigns.svg';

export const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: 0,
      alignItems: 'flex-start',
    },
    campaignContent: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flex: 1,
      minWidth: 0,
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    },
    statusIcon: {
      fontSize: 16,
    },
    scoringProgress: {
      color: '#5EA1F2',
    },
  })
);

interface CampaignItemProps {
  campaign: CampaignProps;
  index: number;
  noDraggable?: boolean;
  showActions?: boolean;
  onSelectCampaign: (campaignId: string) => void;
  onEditCampaign?: (campaign: CampaignProps) => void;
  onDeleteCampaign?: (campaign: CampaignProps) => void;
  onShareCampaign?: (campaign: CampaignProps) => void;
  onLeaveCampaign?: (campaign: CampaignProps) => void;
  /** Recipient-side incoming share label. */
  hideSharedLabel?: boolean;
  /** Owner-side: this resource is shared with other orgs. */
  isSharedOut?: boolean;
  /** Owner-side org count from GET /share/owned. */
  sharedOutOrganizationCount?: number;
}

export const CampaignItem = ({
  campaign,
  index,
  noDraggable = false,
  showActions = false,
  onSelectCampaign,
  onEditCampaign,
  onDeleteCampaign,
  onShareCampaign,
  onLeaveCampaign,
  hideSharedLabel = false,
  isSharedOut = false,
  sharedOutOrganizationCount,
}: CampaignItemProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const isShared = Boolean(campaign.isShared);
  const showIncomingSharedLabel = isShared && !hideSharedLabel;
  const showOutgoingSharedLabel = !isShared && isSharedOut;
  const showSharedLabel = showIncomingSharedLabel || showOutgoingSharedLabel;
  const canMutate = showActions && !isShared;
  const showShare = Boolean(onShareCampaign && !isShared);
  const showLeave = Boolean(onLeaveCampaign && isShared);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onEditCampaign?.(campaign);
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onDeleteCampaign?.(campaign);
  };

  const handleShare = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onShareCampaign?.(campaign);
  };

  const handleLeave = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onLeaveCampaign?.(campaign);
  };

  const actionsMenu =
    canMutate || showShare || showLeave ? (
      <div onClick={(e) => e.stopPropagation()}>
        <IconButton
          onClick={handleOpenMenu}
          style={{ padding: 2, marginLeft: 4 }}
          size='small'
        >
          <MoreVertIcon htmlColor='#757575' style={{ fontSize: 16 }} />
        </IconButton>
        <Menu
          id={`campaign-item-menu-${campaign.id}`}
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          onClick={(e) => e.stopPropagation()}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          {canMutate && <MenuItem onClick={handleEdit}>Edit</MenuItem>}
          {showShare && <MenuItem onClick={handleShare}>Share</MenuItem>}
          {canMutate && (
            <MenuItem onClick={handleDelete} style={{ color: '#ff0000' }}>
              Delete
            </MenuItem>
          )}
          {showLeave && (
            <MenuItem onClick={handleLeave} style={{ color: '#ff0000' }}>
              Leave
            </MenuItem>
          )}
        </Menu>
      </div>
    ) : null;

  const row = (
    <>
      <ListItemIcon style={{ minWidth: 30 }}>
        <CampaignsIcon
          stroke='#757575'
          fill='none'
          strokeWidth={1}
          strokeLinecap='round'
          strokeLinejoin='round'
          style={{ width: 16, height: 16 }}
        />
      </ListItemIcon>
      <Box className={classes.campaignContent}>
        <Tooltip title={campaign.name} placement='top'>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '18px',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {campaign.name}
          </Typography>
        </Tooltip>
        {showSharedLabel && (
          <SharedIndicator
            title={showIncomingSharedLabel ? 'Shared with you' : undefined}
            organizationCount={
              showOutgoingSharedLabel ? sharedOutOrganizationCount : undefined
            }
            onClick={
              showOutgoingSharedLabel && onShareCampaign
                ? () => onShareCampaign(campaign)
                : undefined
            }
          />
        )}
        <Box className={classes.iconContainer}>
          {campaign.scoringStatus === 'IN_PROGRESS' && (
            <Tooltip title='Scoring...' placement='top'>
              <CircularProgress
                size={12}
                thickness={5}
                className={classes.scoringProgress}
              />
            </Tooltip>
          )}
          <Typography
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: '#667085',
              marginRight: 8,
            }}
          >
            {campaign.alignmentRequired === false ||
            campaign.alignmentRequired === undefined
              ? 'S'
              : 'S&E'}
          </Typography>
          {campaign.status === 'completed' ? (
            <CheckCircleIcon
              className={classes.statusIcon}
              style={{ color: '#10B981' }}
              titleAccess='Complete'
            />
          ) : (
            <HourglassEmptyIcon
              className={classes.statusIcon}
              style={{ color: '#F59E0B' }}
              titleAccess={
                campaign.status === 'pending'
                  ? 'Pending'
                  : campaign.status === 'failed'
                  ? 'Failed'
                  : 'Processing'
              }
            />
          )}
          {actionsMenu}
        </Box>
      </Box>
    </>
  );

  if (noDraggable || isShared) {
    return (
      <ListItem
        button
        className={classes.container}
        onClick={() => {
          onSelectCampaign(campaign.id);
        }}
      >
        {row}
      </ListItem>
    );
  }

  return (
    <Draggable draggableId={campaign.id} index={index}>
      {(provided) => (
        <ListItem
          button
          className={classes.container}
          onClick={() => {
            onSelectCampaign(campaign.id);
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          {row}
        </ListItem>
      )}
    </Draggable>
  );
};
